// Background-reprice progress for the open quote (large quotes, Tablerow.RepriceLineLimit).
// Fed by repriceQuote (start + the page it priced up front, as a synthetic batch_done) and by RepriceQuoteFlow's
// SignalR `quote_repricing` pushes: batch_done / batch_failed per batch, then
// completed / completed_with_failures / Failed once at the end.

export const REPRICE_STATUS = {
  RUNNING: 'running',
  COMPLETED: 'completed',
  COMPLETED_WITH_FAILURES: 'completed_with_failures',
  FAILED: 'failed'
}

const union = (a = [], b = []) => [...new Set([...a, ...b])]
const without = (a = [], b = []) => {
  const drop = new Set(b)
  return a.filter((n) => !drop.has(n))
}

const freshProgress = (quoteNo, reason = null) => ({
  quote_no: quoteNo,
  reason,
  status: REPRICE_STATUS.RUNNING,
  done_line_nos: [],
  failed_line_nos: [],
  message: null,
  updated_at: Date.now()
})

const normalizeQuote = (quoteNo) => String(quoteNo || '').toUpperCase()

// A line is either done or failed, never both — the latest report for a line replaces any earlier
// one (e.g. a page refresh marked it done, then the socket says it failed).
const recordLines = (progress, doneNos = [], failedNos = []) => ({
  ...progress,
  done_line_nos: union(without(progress.done_line_nos, failedNos), without(doneNos, failedNos)),
  failed_line_nos: union(without(progress.failed_line_nos, doneNos), failedNos)
})

// A batch push delivered late (SignalR doesn't guarantee order) for a run that already finished or
// was closed must not reopen the banner — it would never get another final push and stay "running".
const LATE_PUSH_WINDOW_MS = 2 * 60 * 1000

const endedRecently = (progress, quoteNo) =>
  progress?.quote_no === quoteNo && progress.ended_at != null && Date.now() - progress.ended_at < LATE_PUSH_WINDOW_MS

// Closed by the user: keep a hidden record so late pushes for this run are ignored.
export const dismissRepriceProgress = (progress) =>
  progress ? { ...progress, dismissed: true, ended_at: progress.ended_at ?? Date.now() } : progress

export const startRepriceProgress = (quoteNo, reason) => freshProgress(normalizeQuote(quoteNo), reason)

// Applies one quote_repricing push. A push for a quote with no running progress (e.g. the page was
// reloaded mid-run) starts tracking it, so the banner still shows.
export const applyRepricePush = (progress, message) => {
  const quoteNo = normalizeQuote(message?.systemId || message?.quote_no)
  if (!quoteNo) return progress

  const isNewRun = !progress || progress.quote_no !== quoteNo
  // Only batch pushes start tracking; a lone final push for an untracked run has no progress to show
  // (the bell still shows its message).
  if (isNewRun && message.status !== 'batch_done' && message.status !== 'batch_failed') return progress
  if (endedRecently(progress, quoteNo)) return progress
  // Past the window, a batch push for a finished/closed run means a new run started elsewhere.
  const startsNewRun = isNewRun || progress.dismissed || progress.status !== REPRICE_STATUS.RUNNING
  let next = startsNewRun ? freshProgress(quoteNo) : { ...progress, updated_at: Date.now() }
  const lineNos = message.line_nos || []
  const failedNos = message.failed_line_nos || []

  switch (message.status) {
    case 'batch_done':
      next = recordLines(next, lineNos, failedNos)
      break
    case 'batch_failed':
      next = recordLines(next, [], lineNos)
      break
    case 'completed':
    case 'completed_with_failures':
      next = recordLines(next, [], failedNos)
      next.status = next.failed_line_nos.length ? REPRICE_STATUS.COMPLETED_WITH_FAILURES : REPRICE_STATUS.COMPLETED
      next.message = message.message || null
      next.ended_at = Date.now()
      break
    case 'Failed':
    case 'failed':
      next = recordLines(next, [], failedNos)
      next.status = REPRICE_STATUS.FAILED
      next.message = message.message || null
      next.ended_at = Date.now()
      break
    default:
      return progress
  }
  return next
}

// Lines the user priced by opening another page mid-run (refresh_line_prices prices + saves them,
// same call the background batches use). Turned into a batch_done push for the progress bar, but
// only while a run is active for this quote and only for lines the socket hasn't reported yet —
// a socket result always wins. Returns null when there's nothing to record.
export const pageRefreshPush = (progress, quoteNo, repricedLines, needsPricing) => {
  const normalized = normalizeQuote(quoteNo)
  if (!progress || progress.status !== REPRICE_STATUS.RUNNING || progress.dismissed || progress.quote_no !== normalized) return null

  const reported = new Set([...progress.done_line_nos, ...progress.failed_line_nos])
  const fresh = (repricedLines || []).filter((line) => line?.line_no != null && !reported.has(line.line_no))
  if (!fresh.length) return null

  return {
    systemId: normalized,
    status: 'batch_done',
    line_nos: fresh.map((line) => line.line_no),
    failed_line_nos: fresh.filter(needsPricing).map((line) => line.line_no)
  }
}

// Segment size for the bar: ~10 segments, rounded to tens (270 lines → 9 × 30).
export const repriceSegmentSize = (total) => Math.max(10, Math.ceil(total / 100) * 10)

const formatRange = (from, to) => (from === to ? `${from}` : `${from}–${to}`)

// Builds the bar segments + summary text the banner shows. Line numbers are treated as 1..total.
export const buildRepriceView = (progress, totalLines) => {
  const total = Math.max(Number(totalLines) || 0, ...(progress?.done_line_nos || []), ...(progress?.failed_line_nos || []))
  if (!progress || total <= 0) return null

  const finished = progress.status !== REPRICE_STATUS.RUNNING
  const done = new Set(progress.done_line_nos)
  const failed = new Set(progress.failed_line_nos)
  const isProcessed = (n) => finished || done.has(n) || failed.has(n)

  const size = repriceSegmentSize(total)
  const segments = []
  let inProgressAssigned = false
  for (let from = 1; from <= total; from += size) {
    const to = Math.min(from + size - 1, total)
    const count = to - from + 1
    let processed = 0
    const failedNos = []
    for (let n = from; n <= to; n++) {
      if (isProcessed(n)) processed++
      if (failed.has(n)) failedNos.push(n)
    }
    // Each unpriced line is marked at its own spot on the segment (left/width in %), so one failed
    // line shows as a thin mark instead of colouring the whole range.
    const failedMarks = failedNos.map((n) => ({ lineNo: n, left: ((n - from) / count) * 100, width: 100 / count }))
    const complete = processed === count
    let state = complete ? 'done' : 'pending'
    // Batches run in parallel; the first unfinished range (or any partially finished one) is "in progress".
    if (!complete && !finished && (processed > 0 || !inProgressAssigned)) {
      state = 'progress'
      inProgressAssigned = true
    }
    segments.push({ from, to, label: formatRange(from, to), state, hasFailed: failedNos.length > 0, failedNos, failedMarks })
  }

  let processedCount = 0
  for (let n = 1; n <= total; n++) if (isProcessed(n)) processedCount++
  const percent = finished ? 100 : Math.min(99, Math.round((processedCount / total) * 100))

  // Contiguous run of finished lines from line 1.
  let prefix = 0
  while (prefix < total && isProcessed(prefix + 1)) prefix++
  const remaining = total - processedCount
  const unpriced = [...failed].filter((n) => n <= total).length

  // Batches run in parallel, so lines rarely finish in order. Use ranges only when every finished
  // line is in one run from line 1 (matches the design); otherwise counts, which always agree with
  // the percentage. The in-progress range starts after the last finished line, so it never overlaps.
  let updatedText
  let inProgressText = null
  if (prefix === processedCount) {
    updatedText = prefix > 0 ? `Lines ${formatRange(1, prefix)} of ${total} updated` : `0 of ${total} lines updated`
    if (remaining > 0) {
      const next = segments.find((s) => s.to > prefix)
      inProgressText = `lines ${formatRange(prefix + 1, next.to)} in progress`
    }
  } else {
    updatedText = `${processedCount} of ${total} lines updated`
    if (remaining > 0) inProgressText = `${remaining} remaining`
  }
  // Every line is in (e.g. the user opened each page) but the run hasn't reported completion yet.
  if (remaining === 0) inProgressText = 'finishing up'

  return {
    total,
    percent,
    segments,
    finished,
    status: progress.status,
    failedLineNos: [...failed].sort((a, b) => a - b),
    updatedText,
    unpricedText: unpriced > 0 ? `${unpriced} not priced` : null,
    inProgressText: finished ? null : inProgressText
  }
}

// "3, 7, 12 and 4 more" — keeps the failure summary to one line.
export const formatLineList = (lineNos, max = 8) => {
  if (lineNos.length <= max) return lineNos.join(', ')
  return `${lineNos.slice(0, max).join(', ')} and ${lineNos.length - max} more`
}
